import React from 'react';
import { Alert, Col, Container, Row, Spinner } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { cameFromState } from './backLink';
import StartApplicationPanel from '@/components/common/StartApplicationPanel';
import ApplicationDeadlineCard from '@/components/common/ApplicationDeadlineCard';
import { useTerm } from '@/shared/contexts/TermContext';
import { useAuth } from '@/shared/contexts/AuthContext';

/**
 * Starting an application without going through the catalog first.
 *
 * A faculty member who already knows they teach CHE 1800 should not have to
 * find it in a list of thousands to reach the form. Browsing stays a first
 * class path for anyone who wants to look, and both arrive at the same step 1.
 */
function StartApplicationPage() {
    const location = useLocation();
    const { currentTerm, currentTermCode, termsLoading } = useTerm();
    const { userProfile } = useAuth();
    const windows = currentTerm?.applicationWindows;
    const noProgramAccepting =
        !!windows && Object.values(windows).every((w) => !w.accepting);

    return (
        <Container className="py-4">
            <Row className="justify-content-center">
                <Col lg={8}>
                    <h1 className="h3 mb-1">Request Support</h1>
                    <p className="text-muted">
                        Ask for peer support for a course you teach. Each
                        program&apos;s application window this term is listed
                        below.
                    </p>

                    <ApplicationDeadlineCard
                        term={currentTerm}
                        facultyEmail={userProfile?.email}
                    />

                    {noProgramAccepting && (
                        <Alert variant="warning">
                            No program is accepting applications from you right
                            now. Each program&apos;s dates are listed above.
                        </Alert>
                    )}

                    {termsLoading && (
                        <div className="text-center py-5">
                            <Spinner
                                animation="border"
                                role="status"
                                variant="primary"
                            >
                                <span className="visually-hidden">
                                    Loading...
                                </span>
                            </Spinner>
                        </div>
                    )}

                    {!termsLoading && !currentTermCode && (
                        <Alert variant="warning">
                            No term is open for applications yet. Check back
                            once the application window has been announced.
                        </Alert>
                    )}

                    {!termsLoading && currentTermCode && (
                        <>
                            <StartApplicationPanel termCode={currentTermCode} />
                            <p className="text-muted small mt-3 mb-0">
                                Not sure which course to pick?{' '}
                                <Link to="/courses">
                                    Browse the full catalog
                                </Link>{' '}
                                and start from there instead.
                            </p>
                            <p className="text-muted small mt-2 mb-0">
                                Teaching an omnibus or special-topics course the
                                catalog does not have?{' '}
                                <Link
                                    to="/applications/new?course=unlisted"
                                    state={cameFromState(location)}
                                >
                                    My course isn&apos;t listed
                                </Link>
                            </p>
                        </>
                    )}
                </Col>
            </Row>
        </Container>
    );
}

export default StartApplicationPage;
